import { X } from 'lucide-react'
import { type ReactNode, useEffect } from 'react'
import { cn } from '../../lib/utils'

export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  badges,
  children,
  width = 'md',
}: {
  open: boolean
  onClose: () => void
  title: string
  subtitle?: ReactNode
  badges?: ReactNode
  children: ReactNode
  width?: 'sm' | 'md' | 'lg'
}) {
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const widths = {
    sm: 'w-full max-w-sm',
    md: 'w-full max-w-md sm:max-w-lg',
    lg: 'w-full max-w-2xl',
  }

  return (
    <div className="fixed inset-0 z-50 flex animate-page-in">
      <button
        type="button"
        onClick={onClose}
        className="absolute inset-0 bg-zinc-950/80 backdrop-blur-md"
        aria-label="Chiudi"
      />
      <aside
        className={cn(
          'ml-auto relative h-full bg-zinc-900/95 border-l border-zinc-800 flex flex-col animate-slide-in-right',
          'shadow-[-24px_0_64px_-12px_rgba(0,0,0,0.7),0_0_0_1px_rgba(34,211,238,0.12)_inset,-12px_0_60px_-20px_rgba(34,211,238,0.3)]',
          widths[width],
        )}
      >
        {/* Left edge cyan glow strip */}
        <div
          aria-hidden
          className="absolute inset-y-0 left-0 w-px bg-gradient-to-b from-transparent via-cyan-500/50 to-transparent"
        />
        <header className="flex items-start justify-between gap-4 px-5 py-4 border-b border-zinc-800/80 shrink-0 bg-zinc-900/60">
          <div className="min-w-0">
            <h2 className="font-display text-[15px] font-semibold tracking-tight text-zinc-50 truncate">
              {title}
            </h2>
            {subtitle && (
              <div className="text-xs text-zinc-400 mt-1 leading-relaxed">{subtitle}</div>
            )}
            {badges && <div className="mt-2.5 flex flex-wrap gap-1.5">{badges}</div>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800/70 rounded-md p-1 transition-colors shrink-0"
            aria-label="Chiudi"
          >
            <X className="size-4" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </aside>
    </div>
  )
}
